import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import frontpageImage from '../../../../layout/images/frontpage_app_background.webp';
import brobizzLogo from '../../../../layout/logos/brobizz-logo-green.svg';
import { H2CamptonBold30 } from '../../../../layout/globalFonts';

// Styled component for the redirect overlay
const RedirectOverlayStyled = styled.div`
  position: fixed;
  width: 100vw;
  height: 100vh;
  background-image: url(${frontpageImage});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
`;

// Styled component for the message box
const MessageBoxStyled = styled.div`
  margin-inline: auto;
  padding: 60px 20px;
  display: grid;
  justify-content: center;
  align-items: start;
  max-width: 500px;
  text-align: center;

  img {
    width: auto;
    height: 45px;
    margin-inline: auto;
    margin-bottom: 45px;
  }

  h1 {
    font-family: Campton Bold;
    font-size: 30px;
    color: #2d2d2d;
    letter-spacing: -0.3px;
    line-height: 32px;
    margin-bottom: 40px;
  }
  h2 {
    font-size: 25px;
  }

  a {
    margin: 0 auto 30px auto;
    font-family: Campton Semi Bold;
    color: #fff !important;
    background-color: #00a050;
    border-color: #00a050;
    border-radius: 100px;
    padding: 12px 36px;
    line-height: 24px;
    display: flex;
    flex-direction: row;
    align-items: center;
    height: unset;
    &:hover {
      background-color: #03944c;
      color: #fff;
      border-color: #03944c;
    }
    &:focus {
      color: #fff;
      background-color: #00a050;
      border-color: #00a050;
    }
  }

  p {
    font-family: Campton Book;
    font-size: 18px;
    color: #2d2d2d;
    margin-bottom: 30px;
    line-height: 24px;
  }
`;

interface MessageData {
  title: string;
  body: string | null;
  linkUrl?: string;
  linkText?: string;
  secondaryTitle?: string;
  secondaryBody?: string;
}

// Function to determine language code
const getBrowserLanguageCode = (): 'da' | 'en' => {
  const browserLang = navigator.language.toLowerCase();
  if (browserLang.startsWith('da')) {
    return 'da';
  }
  return 'en';
};

// Base URL for CDN content - user updated this
const CDN_BASE_URL = 'https://stprodfrontend001.z6.web.core.windows.net/assets';
const CDN_TEXT_FILENAME = 'app-redirect-fallback-text.json';

// React component for the redirect message screen
const RedirectMessageScreen: React.FC = () => {
  const langCode = getBrowserLanguageCode();
  const [messageData, setMessageData] = useState<MessageData>();

  useEffect(() => {
    const fetchCdnMessage = async () => {
      try {
        const response = await fetch(`${CDN_BASE_URL}/${CDN_TEXT_FILENAME}`);
        if (!response.ok) {
          // TODO: Handle !response.ok case
          return;
        }
        const data = await response.json();
        const langSpecificTexts = data[langCode];

        if (langSpecificTexts) {
          setMessageData({
            title: langSpecificTexts.title || '',
            body: langSpecificTexts.body || '',
            linkUrl: langSpecificTexts.linkUrl || '',
            linkText: langSpecificTexts.linkText || '',
            secondaryTitle: langSpecificTexts.secondaryTitle || '',
            secondaryBody: langSpecificTexts.secondaryBody || ''
          });
        }
      } catch (error) {
        // TODO: Handle error case
      }
    };

    fetchCdnMessage();
  }, [langCode]);

  return (
    <RedirectOverlayStyled>
      <MessageBoxStyled>
        <img src={brobizzLogo} alt="Brobizz logo" />
        <h1>{messageData?.title}</h1>
        <p>{messageData?.body}</p>
        <a href={messageData?.linkUrl} target="_blank" rel="noreferrer">
          {messageData?.linkText}
        </a>
        <H2CamptonBold30>{messageData?.secondaryTitle}</H2CamptonBold30>
        <p>{messageData?.secondaryBody}</p>
      </MessageBoxStyled>
    </RedirectOverlayStyled>
  );
};

export default RedirectMessageScreen;
